/**
 * CardShellz Review Provider — Provider-Agnostic Adapter
 *
 * To swap review services, replace the adapter internals below.
 * The public interface (CSZReviews) stays the same.
 *
 * Normalized Review shape:
 *   { id, author, rating, title, body, createdAt, verified, variant, images, productTitle, productHandle }
 */
(function () {
  'use strict';
  if (window.CSZReviews) return;

  var configEl = document.getElementById('csz-review-config');
  if (!configEl) return;

  var provider = configEl.getAttribute('data-provider') || '';
  var shopDomain = configEl.getAttribute('data-shop-domain') || '';
  var _configToken = configEl.getAttribute('data-api-token') || '';

  var cache = {};
  var STORAGE_TTL = 24 * 60 * 60 * 1000; /* 24-hour sessionStorage TTL */

  /* sessionStorage cache — instant repeat loads */
  function storageGet(key) {
    try {
      var raw = sessionStorage.getItem('csz_rv_' + key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (Date.now() - obj.ts > STORAGE_TTL) { sessionStorage.removeItem('csz_rv_' + key); return null; }
      return obj.data;
    } catch (e) { return null; }
  }

  function storageSet(key, data) {
    try { sessionStorage.setItem('csz_rv_' + key, JSON.stringify({ ts: Date.now(), data: data })); } catch (e) { /* quota */ }
  }

  function getApiToken() {
    if (_configToken) return _configToken;
    if (window.jdgm) {
      var t = window.jdgm.PUBLIC_TOKEN || window.jdgm.api_token || '';
      if (t) { _configToken = t; return t; }
    }
    var scripts = document.querySelectorAll('script[data-cfasync]');
    for (var i = 0; i < scripts.length; i++) {
      var match = scripts[i].textContent.match(/PUBLIC_TOKEN\s*=\s*['"]([^'"]+)['"]/);
      if (match) { _configToken = match[1]; return match[1]; }
    }
    return '';
  }

  /**
   * Wait for an API token to become available — race-condition safe.
   *
   * On the storefront, Judge.me's app block (which sets window.jdgm.PUBLIC_TOKEN)
   * often loads AFTER this provider script, especially with deferred script
   * loading. If a fetch fires before Judge.me has set window.jdgm, the token
   * comes back empty and the fetch short-circuits to an empty result.
   *
   * This poller checks getApiToken() every 50ms up to a 3-second cap. Used
   * by all fetch methods below as a wrapper before the actual API call.
   */
  function awaitApiToken(timeoutMs) {
    timeoutMs = timeoutMs || 3000;
    var token = getApiToken();
    if (token) return Promise.resolve(token);

    return new Promise(function (resolve) {
      var start = Date.now();
      var iv = setInterval(function () {
        var t = getApiToken();
        if (t || Date.now() - start >= timeoutMs) {
          clearInterval(iv);
          resolve(t);
        }
      }, 50);
    });
  }

  function cacheKey(prefix, id, opts) {
    return prefix + ':' + id + ':' + (opts.page || 1) + ':' + (opts.perPage || 5) + ':' + (opts.sort || 'newest');
  }

  /* ──────────────────────────────────────────────
     Judge.me Adapter — Direct Widget API (~500ms)
     ────────────────────────────────────────────── */
  function judgemeAdapter() {
    var WIDGET_BASE = 'https://judge.me/api/v1/widgets';

    function buildUrl(path, params) {
      var parts = [];
      var token = getApiToken();
      if (token) parts.push('api_token=' + encodeURIComponent(token));
      parts.push('shop_domain=' + encodeURIComponent(shopDomain));
      for (var key in params) {
        if (params.hasOwnProperty(key) && params[key] !== undefined && params[key] !== null && params[key] !== '') {
          parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
        }
      }
      return WIDGET_BASE + path + '?' + parts.join('&');
    }

    function fetchWidget(url, perPage) {
      return fetch(url).then(function (r) {
        if (!r.ok) {
          return r.text().then(function (body) {
            throw new Error('HTTP ' + r.status + ': ' + body.substring(0, 200));
          });
        }
        return r.json();
      }).then(function (data) {
        return { raw: data, perPage: perPage };
      }).then(function (wrapped) {
        var data = wrapped.raw;
        /* Judge.me uses different top-level keys for different widget endpoints:
             /product_review        → data.widget
             /all_reviews_page      → data.all_reviews
             /featured_carousel     → data.widget (and sometimes data.html)
           Previously this only checked .widget, so /all_reviews_page silently
           returned an empty result — section showed "No reviews yet" even
           though Judge.me returned 87KB of real review HTML. */
        var html = (data && (data.widget || data.all_reviews || data.html)) || '';
        var result = html
          ? parseWidgetHtml(html, data)
          : { reviews: [], average: 0, count: 0, distribution: [0,0,0,0,0], totalPages: 1 };
        return applyHeaderAggregate(result, data, wrapped.perPage);
      });
    }

    /* The /all_reviews_page response carries the store-wide aggregate in a
       separate all_reviews_header key, and its review HTML contains no
       .jdgm-paginate__page elements at all — so parseWidgetHtml alone
       reports totalPages: 1 and the pager never renders, even though the
       endpoint pages correctly (verified: page=2 returns a different set).
       This folds the header aggregate back in and derives totalPages from
       count / perPage. */
    function applyHeaderAggregate(result, data, perPage) {
      var header = (data && data.all_reviews_header) || '';
      if (!header) return result;
      var avg = header.match(/data-average-rating=['"]([\d.]+)/);
      var cnt = header.match(/data-number-of-reviews=['"](\d+)/);
      if (avg) result.average = parseFloat(avg[1]);
      if (cnt) {
        result.count = parseInt(cnt[1], 10);
        if (perPage > 0) {
          result.totalPages = Math.max(result.totalPages || 1, Math.ceil(result.count / perPage));
        }
      }
      return result;
    }

    function parseWidgetHtml(html, meta) {
      var container = document.createElement('div');
      container.innerHTML = html;
      var reviews = [];

      var cards = container.querySelectorAll('.jdgm-rev');
      cards.forEach(function (card) {
        var starEl = card.querySelector('[data-score]');
        var rating = starEl ? parseFloat(starEl.getAttribute('data-score')) : 5;
        var authorEl = card.querySelector('.jdgm-rev__author');
        var titleEl = card.querySelector('.jdgm-rev__title');
        var bodyEl = card.querySelector('.jdgm-rev__body');
        var dateEl = card.querySelector('.jdgm-rev__timestamp');
        var verifiedEl = card.querySelector('.jdgm-rev__buyer-badge') || card.querySelector('.jdgm-verified');
        var imgEls = card.querySelectorAll('.jdgm-rev__media img, .jdgm-rev-media__img');
        var images = [];
        imgEls.forEach(function (img) {
          var src = img.getAttribute('data-src') || img.getAttribute('src') || '';
          if (src && src.indexOf('data:') !== 0) images.push(src);
        });

        reviews.push({
          id: card.getAttribute('data-review-id') || String(reviews.length),
          author: authorEl ? authorEl.textContent.trim() : 'Anonymous',
          rating: rating,
          title: titleEl ? titleEl.textContent.trim() : '',
          body: bodyEl ? bodyEl.textContent.trim() : '',
          createdAt: dateEl ? dateEl.textContent.trim() : '',
          verified: !!verifiedEl,
          variant: null,
          images: images,
          productTitle: '',
          productHandle: ''
        });
      });

      /* Extract aggregate from widget wrapper */
      var average = 0, totalCount = reviews.length, distribution = [0,0,0,0,0];
      var widgetEl = container.querySelector('[data-average-rating]');
      if (widgetEl) {
        average = parseFloat(widgetEl.getAttribute('data-average-rating')) || 0;
        totalCount = parseInt(widgetEl.getAttribute('data-number-of-reviews'), 10) || reviews.length;
      }

      /* Parse distribution histogram */
      var histBars = container.querySelectorAll('[data-frequency]');
      if (histBars.length >= 5) {
        for (var i = 0; i < 5; i++) {
          distribution[i] = parseInt(histBars[i].getAttribute('data-frequency'), 10) || 0;
        }
      }

      /* Parse pagination */
      var totalPages = 1;
      var pages = container.querySelectorAll('.jdgm-paginate__page');
      pages.forEach(function (p) {
        var num = parseInt(p.textContent.trim(), 10);
        if (num > totalPages) totalPages = num;
      });

      return { reviews: reviews, average: average, count: totalCount, distribution: distribution, totalPages: totalPages };
    }

    function logTokenWarning() {
      if (!getApiToken()) {
        console.warn('[CSZReviews] No API token. Add your Judge.me Public Token in Theme Settings → CardShellz Reviews.');
      }
    }

    return {
      provider: 'judgeme',

      fetchProductReviews: function (externalId, opts) {
        opts = opts || {};
        var handle = opts.handle || '';
        var key = cacheKey('product', externalId || handle, opts);
        if (cache[key]) return Promise.resolve(cache[key]);
        var stored = storageGet(key);
        if (stored) { cache[key] = stored; return Promise.resolve(stored); }

        // Await the token (Judge.me's window.jdgm may load AFTER us).
        return awaitApiToken().then(function (token) {
          if (!token) {
            logTokenWarning();
            return {
              reviews: [], average: 0, count: 0,
              distribution: [0, 0, 0, 0, 0],
              currentPage: 1, totalPages: 1,
              _noToken: true
            };
          }

          var productUrl = 'https://' + shopDomain + '/products/' + handle;
          var params = { page: opts.page || 1, per_page: opts.perPage || 5 };
          if (handle) { params.handle = handle; params.url = productUrl; }
          if (externalId) { params.external_id = externalId; }

          return fetchWidget(buildUrl('/product_review', params))
          .then(function (data) {
            if (opts.sort === 'highest') {
              data.reviews.sort(function (a, b) { return b.rating - a.rating; });
            } else if (opts.sort === 'lowest') {
              data.reviews.sort(function (a, b) { return a.rating - b.rating; });
            }
            var result = {
              reviews: data.reviews,
              average: data.average,
              count: data.count,
              distribution: data.distribution,
              currentPage: opts.page || 1,
              totalPages: data.totalPages
            };
            cache[key] = result;
            storageSet(key, result);
            return result;
          })
          .catch(function (err) {
            console.error('[CSZReviews] Fetch error:', err.message);
            return {
              reviews: [], average: 0, count: 0,
              distribution: [0, 0, 0, 0, 0],
              currentPage: 1, totalPages: 1
            };
          });
        }); /* closes awaitApiToken().then */
      },

      fetchFeaturedReviews: function (opts) {
        opts = opts || {};
        var limit = opts.limit || 6;
        var key = 'featured:' + limit;
        if (cache[key]) return Promise.resolve(cache[key]);

        return awaitApiToken().then(function (token) {
          if (!token) { logTokenWarning(); return []; }
          return fetchWidget(buildUrl('/featured_carousel', {}))
            .then(function (data) {
              var reviews = data.reviews.slice(0, limit);
              cache[key] = reviews;
              return reviews;
            })
            .catch(function () { return []; });
        });
      },

      fetchAllReviews: function (opts) {
        opts = opts || {};
        var key = cacheKey('all', 'shop', opts);
        if (cache[key]) return Promise.resolve(cache[key]);

        return awaitApiToken().then(function (token) {
          if (!token) { logTokenWarning(); return { reviews: [], total: 0, currentPage: 1, totalPages: 1 }; }

          /* Judge.me's /all_reviews_page honors per_page (verified via curl:
             per_page=5 → 22KB response, per_page=20 → 72KB). Without it,
             the API returns Judge.me's default and the section ignores
             the operator's "reviews per page" slider entirely. */
          var perPage = opts.perPage || 5;
          return fetchWidget(buildUrl('/all_reviews_page', {
            page: opts.page || 1,
            per_page: perPage,
            review_type: 'product-reviews'
          }), perPage).then(function (data) {
          if (opts.sort === 'highest') {
            data.reviews.sort(function (a, b) { return b.rating - a.rating; });
          } else if (opts.sort === 'lowest') {
            data.reviews.sort(function (a, b) { return a.rating - b.rating; });
          }
          var result = {
            reviews: data.reviews,
            total: data.count,
            currentPage: opts.page || 1,
            totalPages: data.totalPages
          };
          cache[key] = result;
          return result;
        }).catch(function () {
          return { reviews: [], total: 0, currentPage: 1, totalPages: 1 };
          });
        }); /* closes awaitApiToken().then */
      }
    };
  }

  /* ──────────────────────────────────────────────
     Provider Selection
     ────────────────────────────────────────────── */
  if (provider === 'judgeme') {
    window.CSZReviews = judgemeAdapter();
  }
})();
